/**
 * 병사 명단의 순수 규칙. 병영(`ui/sortie.ts`)이 한 명씩 보여 주고, 시험이 DOM 없이 돌린다.
 *
 * 병영은 처음에 병종 묶음이었다(병종 줄 + 펼치는 이름 명단). 아군을 작게 가면서 병사
 * 하나하나가 레벨로 자라게 됐고, 묶음으로는 "누가 몇 레벨이고 누가 진급할 수 있는지"가
 * 보이지 않았다. 그래서 한 명씩 늘어놓는다.
 */
import { Branch, LEVEL_MAX, TROOP_BY_ID, levelOf, levelXp } from '../sim/troops.ts';
import type { TroopDef } from '../sim/troops.ts';
import { soldierLevel } from './army.ts';
import type { Army, Soldier } from './army.ts';

/** 다음 레벨까지 어디쯤인가. 최고 레벨이면 `span`이 0이다. */
export interface LevelProgress {
  readonly level: number;
  /** 이번 레벨에 들어와 쌓은 경험치. */
  readonly into: number;
  /** 이번 레벨에서 다음 레벨까지 필요한 경험치. 최고 레벨이면 0. */
  readonly span: number;
  /** 0–100 정수. 최고 레벨이면 100. */
  readonly pct: number;
}

export function levelProgress(xp: number): LevelProgress {
  const level = levelOf(xp);
  if (level >= LEVEL_MAX) return { level, into: 0, span: 0, pct: 100 };
  const base = levelXp(level);
  const span = levelXp(level + 1) - base;
  const into = xp - base;
  return { level, into, span, pct: Math.floor((into * 100) / span) };
}

/** 가지 순서. 명단과 거르기 칩이 같은 순서를 쓴다. */
export const BRANCH_ORDER: readonly Branch[] = [
  Branch.Spear,
  Branch.Blade,
  Branch.Bow,
  Branch.Horse,
  Branch.Levy,
];

/**
 * 명단 순서: 티어 높은 병사 → 가지 순서 → 레벨 높은 병사 → 먼저 뽑힌 병사.
 * 마지막 uid는 순서가 늘 같게 하려는 것이다. 같은 줄에서 병사가 자리를 바꾸면 누른
 * 자리를 잃는다.
 */
export function rosterOrder(soldiers: readonly Soldier[]): Soldier[] {
  const branchRank = (s: Soldier): number => BRANCH_ORDER.indexOf(TROOP_BY_ID.get(s.troop)!.branch);
  return [...soldiers].sort((a, b) => {
    const ta = TROOP_BY_ID.get(a.troop)!;
    const tb = TROOP_BY_ID.get(b.troop)!;
    return (
      tb.tier - ta.tier ||
      branchRank(a) - branchRank(b) ||
      soldierLevel(b) - soldierLevel(a) ||
      a.uid - b.uid
    );
  });
}

export interface PromotionOption {
  readonly to: TroopDef;
  /** 지금 누를 수 있는가. */
  readonly ok: boolean;
  /** 못 누르는 까닭. 누를 수 있으면 null. */
  readonly why: 'level' | 'gold' | null;
}

/** 이 병사가 갈 수 있는 윗 병종과, 지금 갈 수 있는지. `promote`(army.ts)와 같은 조건이다. */
export function promotionOptions(army: Army, s: Soldier): PromotionOption[] {
  const from = TROOP_BY_ID.get(s.troop);
  if (!from) return [];
  const levelOk = soldierLevel(s) >= from.levelToUpgrade;
  const out: PromotionOption[] = [];
  for (const id of from.upgradesTo) {
    const to = TROOP_BY_ID.get(id);
    if (!to) continue;
    const why = !levelOk ? 'level' : army.gold < to.upgradeCost ? 'gold' : null;
    out.push({ to, ok: why === null, why });
  }
  return out;
}

/**
 * 한꺼번에 올릴 진급: **갈 곳이 하나뿐인**(`upgradesTo`가 하나) 병종에서 레벨이 찬 병사. 갈 곳이 둘 이상이면
 * (민병 → 창·도·궁, 2티어 창 → 3티어 창·기병) 사람이 골라야 하므로 뺀다. 순서는 **값이 싼 진급부터**(같으면
 * 명단 순서) — 돈이 모자라면 앞에서부터 되므로 한 번에 가장 많이 올린다. 처음에는 명단 순서(높은 티어 먼저)라
 * 450냥으로 360냥짜리 하나만 올리고 120냥짜리 넷을 남겼다(싼 것부터면 셋을 올린다). 플레이한 사람이 "부대별로 경로가 하나인 병사를
 * 한 번에" 청했다.
 */
export function batchPromotions(soldiers: readonly Soldier[]): { uid: number; to: TroopDef }[] {
  const out: { uid: number; to: TroopDef }[] = [];
  for (const s of rosterOrder(soldiers)) {
    const from = TROOP_BY_ID.get(s.troop);
    if (!from || from.upgradesTo.length !== 1) continue;
    if (soldierLevel(s) < from.levelToUpgrade) continue;
    const to = TROOP_BY_ID.get(from.upgradesTo[0]);
    if (to) out.push({ uid: s.uid, to });
  }
  // 안정 정렬이라 값이 같으면 명단 순서가 남는다.
  return out.sort((x, y) => x.to.upgradeCost - y.to.upgradeCost);
}

/** `batchPromotions`의 앞에서부터 돈이 되는 만큼. 몇 명을 얼마에 올릴 수 있는가(아직 올리지 않는다). */
export function batchAffordable(army: Army, list: readonly { to: TroopDef }[]): { count: number; cost: number } {
  let gold = army.gold;
  let count = 0;
  let cost = 0;
  for (const p of list) {
    if (gold < p.to.upgradeCost) break;
    gold -= p.to.upgradeCost;
    cost += p.to.upgradeCost;
    count++;
  }
  return { count, cost };
}


/**
 * 갈 곳이 **여럿인** 병종(민병 → 창·도·궁, 2티어 창 → 3티어 창·기병)에서 레벨이 찬 병사를, 병종마다 한 묶음으로.
 * 묶음마다 갈 곳(`targets`)을 적는다 — 화면이 "민병 6명 → [창수] [도부수] [궁수]"로 보이고, 하나를 누르면 그
 * 묶음이 모두 그리로 오른다. 병사 순서는 명단 순서(`rosterOrder`), 묶음 순서는 그 병종이 처음 나온 순서.
 *
 * `batchPromotions`는 갈 곳이 하나뿐인 병종만 올린다(사람이 고를 것이 없다). 새 부대를 민병으로 꾸리면 그것으로
 * 올릴 병사가 하나도 없어 한 명씩 상세를 열어야 했다 — 플레이한 사람이 "여러 경로인 병사들도 편하게"를 청했다.
 */
export function branchingPromotions(soldiers: readonly Soldier[]): { from: TroopDef; uids: number[]; targets: TroopDef[] }[] {
  const groups = new Map<string, { from: TroopDef; uids: number[]; targets: TroopDef[] }>();
  const out: { from: TroopDef; uids: number[]; targets: TroopDef[] }[] = [];
  for (const s of rosterOrder(soldiers)) {
    const from = TROOP_BY_ID.get(s.troop);
    if (!from || from.upgradesTo.length < 2) continue;
    if (soldierLevel(s) < from.levelToUpgrade) continue;
    let g = groups.get(from.id);
    if (!g) {
      const targets = from.upgradesTo.map((id) => TROOP_BY_ID.get(id)).filter((t): t is TroopDef => !!t);
      g = { from, uids: [], targets };
      groups.set(from.id, g);
      out.push(g);
    }
    g.uids.push(s.uid);
  }
  return out;
}

/** 한 묶음을 `to`로 올릴 때 돈이 되는 만큼: 앞에서부터 몇 명을 얼마에(아직 올리지 않는다). */
export function branchAffordable(army: Army, n: number, to: TroopDef): { count: number; cost: number } {
  const count = to.upgradeCost <= 0 ? n : Math.min(n, Math.floor(army.gold / to.upgradeCost));
  return { count, cost: count * to.upgradeCost };
}
